<script setup lang="ts">
import FormField from '@form/form-field.vue';
import FormOtpInput from '@form/form-otp-input.vue';
import ButtonPrimary from '@buttons/button-primary.vue';
import PartIconCircle from '@parts/part-icon-circle.vue';
import CoreIcon from '@core/core-icon.vue';
import CoreTimer from '@core/core-timer.vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

defineProps<{
	step: 'email' | 'otp';
	loginForm: { email: string };
	sendOtpLoading: boolean;
	verifyOtpLoading: boolean;
	otpCode: string;
	otpError: boolean;
	canResend: boolean;
	timerKey: number;
	timerDuration: number;
	validation: any;
}>();

const emit = defineEmits<{
	'send-otp': [];
	'verify-otp': [];
	'resend-otp': [];
	'go-back': [];
	'update:email': [value: string];
	'update:otp-code': [value: string];
	'social-auth': [provider: string];
	'timer-end': [];
}>();
</script>

<template>
	<section class="section-welcome-auth">
		<!-- Step 1: Enter email -->
		<transition
			v-if="step === 'email'"
			appear
			name="show-up-container-items"
			:style="`--animation-delay: 0s;
					--animation-item-delay: 0.1s;
					--animation-duration: 0.5s;
					--animation-count: ${4}`"
		>
			<form
				action=""
				class="section-welcome-auth__form"
				@submit.prevent="emit('send-otp')"
			>
				<div class="section-welcome-auth__socials">
					<button
						type="button"
						class="section-welcome-auth__social-btn"
						@click="emit('social-auth', 'telegram')"
					>
						<img src="/images/telegram.svg" alt="Telegram" class="section-welcome-auth__social-icon" />
						<span>{{ t('welcome.auth.telegram') }}</span>
					</button>
					<button
						type="button"
						class="section-welcome-auth__social-btn"
						@click="emit('social-auth', 'google')"
					>
						<img src="/images/google.svg" alt="Google" class="section-welcome-auth__social-icon" />
						<span>{{ t('welcome.auth.google') }}</span>
					</button>
				</div>

				<div class="section-welcome-auth__divider">
					<hr class="section-welcome-auth__divider-line" />
					<span class="section-welcome-auth__divider-text">{{ t('welcome.auth.divider') }}</span>
					<hr class="section-welcome-auth__divider-line" />
				</div>

				<form-field
					name="email"
					:model-value="loginForm.email"
					@update:model-value="emit('update:email', $event)"
					:placeholder="t('welcome.auth.email_placeholder')"
					:error="validation.email?.$errors && validation.email.$errors.length > 0"
					label-font="font-regular"
					class="section-welcome-auth__field"
				/>

				<button-primary
					:loading="sendOtpLoading"
					:icon="{ name: 'arrowRight', viewBox: '0 0 40 40' }"
					class="section-welcome-auth__submit"
				>
					{{ t('welcome.auth.continue') }}
				</button-primary>
			</form>
		</transition>

		<!-- Step 2: OTP -->
		<transition
			v-else
			appear
			name="show-up-container-items"
			:style="`--animation-delay: 0s;
					--animation-item-delay: 0.1s;
					--animation-duration: 0.5s;
					--animation-count: ${5}`"
		>
			<form
				action=""
				class="section-welcome-auth__form"
				@submit.prevent="emit('verify-otp')"
			>
				<form-field
					name="email"
					:model-value="loginForm.email"
					disabled
					label-font="font-regular"
					class="section-welcome-auth__field"
				/>

				<div class="section-welcome-auth__otp-section">
					<div class="section-welcome-auth__otp-header">
						<h3 class="section-welcome-auth__otp-title">{{ t('welcome.otp.title') }}</h3>
						<p class="section-welcome-auth__otp-subtitle">{{ t('welcome.otp.subtitle', { email: loginForm.email }) }}</p>
					</div>
					<form-otp-input
						:model-value="otpCode"
						@update:model-value="emit('update:otp-code', $event)"
						:error="otpError"
					/>
				</div>

				<div class="section-welcome-auth__resend">
					<template v-if="canResend">
						<span
							class="section-welcome-auth__resend-link"
							@click="emit('resend-otp')"
						>
							<core-icon
								name="restart"
								viewBox="0 0 20 20"
								class="section-welcome-auth__resend-icon"
							/>
							{{ $t('login.otp.resend') }}
						</span>
					</template>
					<template v-else>
						<span class="section-welcome-auth__resend-timer">
							{{ $t('login.otp.resend_timer') }}
							<core-timer
								:key="timerKey"
								:duration="timerDuration"
								@end="emit('timer-end')"
							/>
						</span>
					</template>
				</div>

				<div class="section-welcome-auth__buttons-row">
					<part-icon-circle
						light
						class="section-welcome-auth__back-button"
						:icon="{ name: 'arrowLeft', viewBox: '0 0 13 22' }"
						@click="emit('go-back')"
					/>
					<button-primary
						:loading="verifyOtpLoading"
						:disabled="otpCode.length < 6"
						:icon="{ name: 'arrowRight', viewBox: '0 0 40 40' }"
						class="section-welcome-auth__submit section-welcome-auth__submit--otp"
					>
						{{ t('welcome.otp.verify') }}
					</button-primary>
				</div>
			</form>
		</transition>
	</section>
</template>

<style lang="scss">
.section-welcome-auth {
	width: 100%;
	max-width: 48rem;
	margin: 0 auto;

	&__form {
		display: flex;
		flex-direction: column;
		gap: 2.4rem;
	}

	&__socials {
		display: flex;
		gap: 1.6rem;
	}

	&__social-btn {
		flex: 1;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 1rem;
		padding: 1.6rem 2.4rem;
		background: rgba(var(--color-active-rgb), 0.06);
		border: 1px solid rgba(var(--color-active-rgb), 0.12);
		border-radius: 10rem;
		cursor: pointer;
		@include font-16-pro-medium;
		color: rgba(var(--color-text-rgb), 1);
		transition: background-color ease 0.17s;

		@include min-desktop {
			height: 6.3rem;
			@include font-18-pro-medium;

			&:hover {
				background: rgba(var(--color-active-rgb), 0.1);
			}
		}
	}

	&__social-icon {
		width: 2.4rem;
		height: 2.4rem;
	}

	&__divider {
		display: flex;
		align-items: center;
		gap: 1.6rem;
	}

	&__divider-line {
		flex: 1;
		border: none;
		border-top: 1px solid rgba(var(--color-active-rgb), 0.12);
	}

	&__divider-text {
		@include font-16-pro-regular;
		color: rgba(var(--color-text-rgb), 0.3);
		white-space: nowrap;

		@include min-desktop {
			@include font-18-pro-medium;
		}
	}

	&__field {
		width: 100%;
	}

	&__submit {
		width: 100%;

		@include min-desktop {
			height: 6.2rem;
		}

		&--otp {
			flex: 1;
		}
	}

	&__otp-section {
		display: flex;
		flex-direction: column;
		gap: 2.4rem;
	}

	&__otp-header {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	&__otp-title {
		@include font-18-pro-medium;
		@include min-desktop {
			@include font-24-pro-bold;
		}
	}

	&__otp-subtitle {
		color: rgba(var(--color-text-rgb), 0.5);
		@include font-16-pro-regular;
	}

	&__resend {
		text-align: center;
	}

	&__resend-link {
		color: var(--color-text);
		cursor: pointer;
		@include font-16-pro-medium;
		transition: opacity ease 0.2s;
		display: inline-flex;
		align-items: center;
		gap: 0.6rem;

		&:hover {
			opacity: 0.7;
		}
	}

	&__resend-icon {
		width: 2rem;
		height: 2rem;
		stroke: var(--color-text);
		fill: none;
	}

	&__resend-timer {
		color: rgba(var(--color-text-rgb), 0.5);
		@include font-16-pro-regular;

		.core-timer {
			display: inline;
		}
	}

	&__buttons-row {
		display: flex;
		gap: 1.6rem;
		align-items: center;
	}

	&__back-button {
		--button-size: 5.2rem;
		--button-icon-size: 0.8rem;
		flex: 0 0 auto;
		cursor: pointer;

		@include min-desktop {
			--button-size: 6.2rem;
		}
	}
}
</style>
